// RBAC role + permission model — source of truth.
//
// Sourced from designer's
//   design/UI : UX v1.0/for-engineering-users/specs/users-strings.md (role table + View 4)
//   design/UI : UX v1.0/for-engineering-shell/specs/shell-strings.md (RBAC nav visibility)
//
// Phase 0 has 5 fixed roles + 14 permissions. Custom roles are post-MVP per
// the Users handoff §4.
//
// Localized labels + descriptions live in the admin app's i18n (NOT here —
// this file is locale-free, shared by frontend + backend).

/** Closed enum of the 5 Phase 0 roles. */
export type RoleKey =
  | 'super_admin'
  | 'content_editor'
  | 'customer_success'
  | 'analyst'
  | 'read_only';

export const ALL_ROLES: ReadonlyArray<RoleKey> = [
  'super_admin',
  'content_editor',
  'customer_success',
  'analyst',
  'read_only',
] as const;

/** Closed enum of the 15 Phase 0 permissions. */
export type Permission =
  | 'kb:read'
  | 'kb:write'
  | 'sources:write'
  | 'frameworks:write'
  | 'analytics:read'
  | 'benchmark:read'
  | 'benchmark:write'
  | 'tenants:read'
  | 'tenants:write'
  | 'moderation:read'
  | 'billing:write'
  | 'users:read'
  | 'users:write'
  | 'settings:read'
  | 'settings:write'
  | 'audit:read';

export const ALL_PERMISSIONS: ReadonlyArray<Permission> = [
  'kb:read',
  'kb:write',
  'sources:write',
  'frameworks:write',
  'analytics:read',
  'benchmark:read',
  'benchmark:write',
  'tenants:read',
  'tenants:write',
  'moderation:read',
  'billing:write',
  'users:read',
  'users:write',
  'settings:read',
  'settings:write',
  'audit:read',
] as const;

/** Permission groups for the Manage Roles permission matrix view (UI grouping only). */
export type PermissionGroup = 'knowledge' | 'insights' | 'customers' | 'admin';

export const PERMISSION_GROUP: Record<Permission, PermissionGroup> = {
  'kb:read': 'knowledge',
  'kb:write': 'knowledge',
  'sources:write': 'knowledge',
  'frameworks:write': 'knowledge',
  'analytics:read': 'insights',
  'benchmark:read': 'insights',
  'benchmark:write': 'insights',
  'tenants:read': 'customers',
  'tenants:write': 'customers',
  'moderation:read': 'customers',
  'billing:write': 'customers',
  'users:read': 'admin',
  'users:write': 'admin',
  'settings:read': 'admin',
  'settings:write': 'admin',
  'audit:read': 'admin',
};

/**
 * Hex swatch per role — sourced from users-strings.md role table. Kept here
 * (not in admin i18n) because the backend's GET /roles serves this verbatim
 * so the frontend doesn't have to duplicate the constant.
 */
export const ROLE_SWATCH: Record<RoleKey, string> = {
  super_admin: '#3F2BB8',
  content_editor: '#0A7A65',
  customer_success: '#2B6CB0',
  analyst: '#B86A0F',
  read_only: '#565869',
};

/**
 * Permission grants per role. Used by both the backend RBAC enforcer + the
 * frontend nav-visibility filter. The frontend filter is a convenience, not
 * a security boundary — the backend re-authorizes every request.
 *
 * Where the read-form of a permission isn't explicitly listed, it's NOT
 * granted (e.g. content_editor has kb:write but also kb:read explicit;
 * customer_success does not have kb:read).
 */
export const ROLE_PERMISSIONS: Record<RoleKey, ReadonlyArray<Permission>> = {
  super_admin: [
    'kb:read',
    'kb:write',
    'sources:write',
    'frameworks:write',
    'analytics:read',
    'benchmark:read',
    'benchmark:write',
    'tenants:read',
    'tenants:write',
    'moderation:read',
    'billing:write',
    'users:read',
    'users:write',
    'settings:read',
    'settings:write',
    'audit:read',
  ],
  content_editor: ['kb:read', 'kb:write', 'sources:write', 'frameworks:write', 'benchmark:read'],
  // Customer Success has tenant + moderation + analytics access but does NOT
  // see the internal Users + Roles menu (per architectural decision 2026-06-07).
  // Only super_admin manages internal staff lifecycle. Reconsider if CS ever
  // needs to look up internal users for support — they currently can't.
  // V1 RBAC for tenants: CS is read-only (list + detail). Only super_admin
  // can create / edit / suspend / remove / restore tenants. If CS later
  // needs to update tenant metadata (e.g. billing email correction), add a
  // narrower 'tenants:edit_metadata' permission instead of broadening
  // tenants:write to CS.
  customer_success: ['tenants:read', 'moderation:read', 'analytics:read'],
  analyst: ['analytics:read', 'benchmark:read', 'audit:read', 'kb:read'],
  read_only: ['kb:read', 'analytics:read', 'audit:read'],
};

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Type guard for the closed role enum. Useful when reading data from Mongo
 * or a JWT (untyped strings). */
export function isRoleKey(value: unknown): value is RoleKey {
  return typeof value === 'string' && (ALL_ROLES as ReadonlyArray<string>).includes(value);
}

/** Filter a list of unknown strings to the known role set. Drops unknowns
 * silently — caller decides whether to log. */
export function parseRoles(values: ReadonlyArray<string> | undefined): RoleKey[] {
  if (!values) return [];
  return values.filter(isRoleKey);
}

/** Does this single role grant this permission? */
export function roleHasPermission(role: RoleKey, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

/** Does ANY of these roles grant this permission? Use when a user has
 * multiple roles (current model: typically one role per user, but the
 * array shape supports future multi-role). */
export function rolesHavePermission(
  roles: ReadonlyArray<RoleKey>,
  permission: Permission,
): boolean {
  return roles.some((role) => roleHasPermission(role, permission));
}

/** Union of all permissions across a role set — useful for UI "permission
 * preview" chips on the Invite drawer. */
export function permissionsForRoles(roles: ReadonlyArray<RoleKey>): ReadonlyArray<Permission> {
  const set = new Set<Permission>();
  for (const role of roles) {
    for (const p of ROLE_PERMISSIONS[role]) set.add(p);
  }
  return Array.from(set);
}
